/**
 * Realtime Provider Factory
 *
 * Selects and instantiates the appropriate provider (Pusher or Centrifugo)
 * based on the VUE_APP_REALTIME_PROVIDER environment variable.
 *
 * Supported values:
 *   - 'pusher'     — Use Pusher SaaS
 *   - 'centrifugo' — Use self-hosted Centrifugo (default)
 */

import { environment } from '@/shared/env'
import { RealtimeProvider } from './types'
import { CentrifugoProvider } from './centrifugoProvider'
import { PusherProvider } from './pusherProvider'

type ProviderType = 'pusher' | 'centrifugo'

/**
 * Determine which provider to use based on environment variable.
 * Defaults to 'centrifugo' if not specified.
 */
function getProviderType(): ProviderType {
  const provider = environment('VUE_APP_REALTIME_PROVIDER')
  if (provider === 'pusher') {
    return 'pusher'
  }
  return 'centrifugo'
}

/**
 * Factory function to create a realtime provider instance.
 * Returns the appropriate provider (Pusher or Centrifugo) based on env config.
 */
export function createRealtimeProvider(): RealtimeProvider {
  const providerType = getProviderType()

  console.info(`[RealtimeProviderFactory] Creating provider: ${providerType}`)

  switch (providerType) {
    case 'pusher':
      return new PusherProvider()
    case 'centrifugo':
      return new CentrifugoProvider()
    default:
      console.warn(`[RealtimeProviderFactory] Unknown provider: ${providerType}, defaulting to centrifugo`)
      return new CentrifugoProvider()
  }
}

export type { RealtimeProvider, RealtimeInitParams } from './types'
export { CentrifugoProvider } from './centrifugoProvider'
export { PusherProvider } from './pusherProvider'
